/**
 * sealed-vinyl funnel resume 정보 — sessionStorage 기반.
 *
 * 사용 케이스: PaymentStep 에서 카드 직접 등록(`/sealed-vinyl/card-register`)
 * 진입 직전에 저장 → /sealed-vinyl 가 unmount 되었다가 navigate(-1) 로 재마운트
 * 될 때 entryStep='payment' 로 복원해 사용자가 결제 화면에 그대로 머무르게 함.
 *
 * 카카오페이 등록 흐름은 외부 redirect(딥링크) 라 webview 가 unmount 되지 않아
 * resume 불필요 — visibility 갱신만으로 cards refetch.
 */

/** 복원 가능한 step — Funnel 의 SealedVinylStep 중 안전한 부분집합 */
const RESUME_STEPS = ['payment'] as const
export type ResumeStep = (typeof RESUME_STEPS)[number]

export interface ResumeContext {
  step: ResumeStep
  /** payment step 으로 복원 시 유지할 신청 수량(1~3). 없으면 기본 수량으로 fallback. */
  quantity?: number
  /** NICEPAY 직접 등록(CardRegisterPage) 직후 복원 시 자동선택할 카드 id. */
  selectedCardId?: string
}

const STORAGE_KEY = 'sealed-vinyl:resume'

function isResumeStep(value: unknown): value is ResumeStep {
  return typeof value === 'string' && (RESUME_STEPS as readonly string[]).includes(value)
}

export function saveResume(context: ResumeContext): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context))
  } catch {
    // iOS Safari Private 모드 등 — 저장 실패해도 무시
  }
}

export function readResume(): ResumeContext | null {
  let raw: string | null = null
  try {
    raw = sessionStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw) as { step?: unknown; quantity?: unknown; selectedCardId?: unknown }
    if (!isResumeStep(parsed.step)) {
      clearResume()
      return null
    }
    const quantity = typeof parsed.quantity === 'number' ? parsed.quantity : undefined
    const selectedCardId = typeof parsed.selectedCardId === 'string' ? parsed.selectedCardId : undefined
    return { step: parsed.step, quantity, selectedCardId }
  } catch {
    clearResume()
    return null
  }
}

export function clearResume(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // no-op
  }
}
